import { useQuery } from '@tanstack/react-query';

import { fetchScheduleEvents, type ScheduleEvent } from '../../services/schedule';
import { useCurrentCompany } from '../useCurrentCompany';

export interface UpcomingClass extends ScheduleEvent {
  dateLabel: string;
}

export function useUpcomingClasses(days = 7) {
  const { data: companyId } = useCurrentCompany();

  const now = new Date();
  const rangeStart = new Date(now);
  rangeStart.setHours(0, 0, 0, 0);
  const rangeEnd = new Date(now);
  rangeEnd.setDate(rangeEnd.getDate() + days);
  rangeEnd.setHours(23, 59, 59, 999);

  const query = useQuery({
    queryKey: ['upcoming-classes', companyId, days],
    queryFn: async () => {
      const events = await fetchScheduleEvents(rangeStart.toISOString(), rangeEnd.toISOString());

      // Filter to future events only and sort chronologically
      const nowMs = Date.now();
      return events
        .filter((e) => e.start_time && new Date(e.start_time).getTime() > nowMs)
        .sort((a, b) => new Date(a.start_time!).getTime() - new Date(b.start_time!).getTime())
        .map((e) => ({
          ...e,
          dateLabel: new Date(e.start_time!).toLocaleDateString([], {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          }),
        }));
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000,
  });

  return {
    classes: (query.data || []) as UpcomingClass[],
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}
